import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, Home } from "lucide-react";
import { AdminClientsTable } from "./AdminClientsTable";
import { AdminSubOrgsTable } from "./AdminSubOrgsTable";
import { AdminPortfoliosTable } from "./AdminPortfoliosTable";
import { CreateOrganizationWizard } from "./CreateOrganizationWizard";

interface Client {
  id: string;
  name: string;
  slug?: string | null;
  created_at?: string;
}

interface SubOrg {
  id: string;
  name: string;
  client_id: string;
  created_at: string;
  archived: boolean;
}

interface AdminOrganizationHierarchyProps {
  onLogAction: (action: string, tableName?: string, recordId?: string, oldValues?: any, newValues?: any, reason?: string) => Promise<void>;
}

type ViewLevel = "clients" | "subOrgs" | "portfolios";

interface NavigationState {
  navigateTo?: "subOrgs" | "portfolios";
  clientId?: string;
  clientName?: string;
  subOrgId?: string;
  subOrgName?: string;
}

export const AdminOrganizationHierarchy = ({ onLogAction }: AdminOrganizationHierarchyProps) => {
  const location = useLocation();
  const [viewLevel, setViewLevel] = useState<ViewLevel>("clients");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSubOrg, setSelectedSubOrg] = useState<SubOrg | null>(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  // Handle navigation state from breadcrumb clicks
  useEffect(() => {
    const navState = location.state as NavigationState | null;
    console.log("[AdminOrganizationHierarchy] Processing nav state:", navState);
    
    if (navState?.navigateTo) {
      if (navState.navigateTo === "subOrgs" && navState.clientId && navState.clientName) {
        // Navigate to sub-orgs view for the specified client
        console.log("[AdminOrganizationHierarchy] Navigating to subOrgs for client:", navState.clientName);
        setSelectedClient({
          id: navState.clientId,
          name: navState.clientName,
          slug: null,
          created_at: "",
        });
        setSelectedSubOrg(null);
        setViewLevel("subOrgs");
      } else if (navState.navigateTo === "portfolios" && navState.clientId && navState.subOrgId) {
        // Navigate to portfolios view for the specified sub-org
        console.log("[AdminOrganizationHierarchy] Navigating to portfolios for subOrg:", navState.subOrgName);
        setSelectedClient({
          id: navState.clientId,
          name: navState.clientName || "",
          slug: null,
          created_at: "",
        });
        setSelectedSubOrg({
          id: navState.subOrgId,
          name: navState.subOrgName || "",
          client_id: navState.clientId,
          created_at: "",
          archived: false,
        });
        setViewLevel("portfolios");
      }
      // Clear the navigation state AFTER processing to prevent re-triggering on other navigations
      // Use setTimeout to ensure state updates have been applied
      setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [location.key]); // Use location.key to detect new navigations

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setSelectedSubOrg(null);
    setViewLevel("subOrgs");
  };

  const handleSelectSubOrg = (subOrg: SubOrg) => {
    setSelectedSubOrg(subOrg);
    setViewLevel("portfolios");
  };

  const navigateToClients = () => {
    setViewLevel("clients");
    setSelectedClient(null);
    setSelectedSubOrg(null);
  };

  const navigateToSubOrgs = () => {
    setViewLevel("subOrgs");
    setSelectedSubOrg(null);
  };

  const handleCreated = () => {
    // Refresh current view after creation
    // The individual tables will reload on mount
    setShowCreateWizard(false);
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb navigation */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 hover:text-foreground"
          onClick={navigateToClients}
        >
          <Home className="h-4 w-4 mr-1" />
          Clients
        </Button>
        
        {selectedClient && (
          <>
            <ChevronRight className="h-4 w-4" />
            <Button
              variant="ghost"
              size="sm"
              className={`h-auto p-1 ${viewLevel === "subOrgs" ? "text-foreground font-medium" : "hover:text-foreground"}`}
              onClick={navigateToSubOrgs}
            >
              {selectedClient.name}
            </Button>
          </>
        )}
        
        {selectedSubOrg && (
          <>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground font-medium px-1">
              {selectedSubOrg.name}
            </span>
          </>
        )}
      </nav>

      {/* Level indicator */}
      <div className="text-lg font-semibold">
        {viewLevel === "clients" && "Client Organizations"}
        {viewLevel === "subOrgs" && `Sub-Organizations under ${selectedClient?.name}`}
        {viewLevel === "portfolios" && `Portfolio Companies under ${selectedSubOrg?.name}`}
      </div>

      {/* Content based on current view level */}
      {viewLevel === "clients" && (
        <AdminClientsTable
          onSelectClient={handleSelectClient}
          onCreateClick={() => setShowCreateWizard(true)}
        />
      )}

      {viewLevel === "subOrgs" && selectedClient && (
        <AdminSubOrgsTable
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          onSelectSubOrg={handleSelectSubOrg}
          onCreateClick={() => setShowCreateWizard(true)}
        />
      )}

      {viewLevel === "portfolios" && selectedSubOrg && selectedClient && (
        <AdminPortfoliosTable
          subOrgId={selectedSubOrg.id}
          subOrgName={selectedSubOrg.name}
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          onCreateClick={() => setShowCreateWizard(true)}
          onLogAction={onLogAction}
        />
      )}

      {/* Create Organization Wizard */}
      <CreateOrganizationWizard
        open={showCreateWizard}
        onOpenChange={setShowCreateWizard}
        onCreated={handleCreated}
        preselectedClientId={selectedClient?.id}
        preselectedClientName={selectedClient?.name}
        preselectedSubOrgId={selectedSubOrg?.id}
        preselectedSubOrgName={selectedSubOrg?.name}
      />
    </div>
  );
};
